import type { RailBranch } from "@/data/rail-branches";
import { branchCurveMs, forkCurveSpan, joinCurveSpan, railLaneOffsetY } from "../RailLanes/lanes";

/** Halfway from the home line to the scene's branch, eased along the curve. */
export function activeBranchOffsetY(
  branches: readonly RailBranch[],
  locationLabel: string,
  playheadMs: number,
): number {
  const branch = branches.find(
    (item) =>
      item.label === locationLabel &&
      playheadMs >= item.startMs &&
      playheadMs < item.endMs,
  );
  if (branch === undefined || branch.lane === 0) {
    return 0;
  }
  const full = railLaneOffsetY(branch.lane, branch.laneOffsets) / 2;
  const curveMs = branchCurveMs(branch);
  if (branch.forkFromLane !== undefined) {
    const t = (playheadMs - branch.startMs) / curveMs;
    if (t >= 0 && t < 1) {
      const from = railLaneOffsetY(branch.forkFromLane, branch.laneOffsets) / 2;
      return lerp(from, full, smooth(t));
    }
  }
  if (branch.joinToLane !== undefined) {
    const t = (branch.endMs - playheadMs) / curveMs;
    if (t >= 0 && t < 1) {
      const to = railLaneOffsetY(branch.joinToLane, branch.laneOffsets) / 2;
      return lerp(to, full, smooth(t));
    }
  }
  return full;
}

export function branchAt(
  branches: readonly RailBranch[],
  rootPlaceId: string,
  playheadMs: number,
): RailBranch | undefined {
  const matches = branches.filter(
    (branch) =>
      branch.rootPlaceId === rootPlaceId &&
      playheadMs >= branch.startMs &&
      playheadMs <= branch.endMs,
  );
  return matches.find((branch) => branch.startMs === playheadMs) ?? matches[0];
}

export function branchOffsetY(
  branch: RailBranch | undefined,
  playheadMs: number,
): number {
  if (branch === undefined) {
    return 0;
  }
  const laneY = railLaneOffsetY(branch.lane, branch.laneOffsets);
  if (branch.forkFromLane !== undefined) {
    const fork = forkCurveSpan(branch);
    const t = (playheadMs - fork.startMs) / (fork.endMs - fork.startMs);
    if (t >= 0 && t < 1) {
      return lerp(railLaneOffsetY(branch.forkFromLane, branch.laneOffsets), laneY, smooth(t));
    }
  }
  if (branch.joinToLane !== undefined) {
    const join = joinCurveSpan(branch);
    const t = (join.endMs - playheadMs) / (join.endMs - join.startMs);
    if (t >= 0 && t < 1) {
      return lerp(railLaneOffsetY(branch.joinToLane, branch.laneOffsets), laneY, smooth(t));
    }
  }
  return laneY;
}

function smooth(t: number): number {
  const clamped = clamp01(t);
  return clamped * clamped * (3 - 2 * clamped);
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
